import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { useAuthContext } from '../OpenRpcWidgetComponent/OpenRpcWidgetComponent';
import { MethodObject } from '@open-rpc/meta-schema';
import { ExamplePairingObject, ExampleObject } from '@open-rpc/meta-schema';

export type TParamStructure = 'either' | 'by-name' | 'by-position';

interface IMethodPluginProps {
  openrpcMethodObject: MethodObject;
}

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    minWidth: '600px',
    maxWidth: '800px',
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  textField: {
    fontFamily: 'monospace',
    fontSize: '12px',
  },
  responseBox: {
    backgroundColor: theme.palette.type === 'dark' ? '#1e1e1e' : '#f5f5f5',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    fontFamily: 'monospace',
    fontSize: '12px',
    overflowX: 'auto',
    maxHeight: '300px',
    overflowY: 'auto',
  },
  errorBox: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    fontFamily: 'monospace',
    fontSize: '12px',
  },
}));

const InspectorPlugin: React.FC<IMethodPluginProps> = props => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const { authToken } = useAuthContext();
  const method = props.openrpcMethodObject;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const examplePosition = 0;
  let exampleParams: any;
  let emptyParams: any = {};

  if (method && method.params) {
    emptyParams = Array.isArray(method.params) ? (method.params as any[]).reduce((memo: any, p: any) => {
      memo[p.name] = null;
      return memo;
    }, {}) : {};
  }

  if (method && method.examples && method.examples[examplePosition]) {
    const example = method.examples[examplePosition] as ExamplePairingObject;
    const paramStructure: TParamStructure = method.paramStructure || 'either';
    if (example.params && (example.params as ExampleObject[]).length > 0) {
      exampleParams =
        paramStructure === 'by-name'
          ? (example.params as ExampleObject[]).reduce((memo, p) => {
              memo[p.name] = p.value;
              return memo;
            }, {} as any)
          : (example.params as ExampleObject[]).map(p => p.value);
    }
  }

  const initialRequest = {
    jsonrpc: '2.0',
    method: method.name,
    params: exampleParams || emptyParams,
    id: 1,
  };

  const [requestBody, setRequestBody] = useState(
    JSON.stringify(initialRequest, null, 2),
  );
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const handleCopyResponse = useCallback(() => {
    if (!response) return;
    navigator.clipboard.writeText(JSON.stringify(response, null, 2)).then(() => {
      setCopied(true);
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      setCopied(false);
    });
  }, [response]);

  const handleOpen = () => {
    setOpen(true);
    setResponse(null);
    setError(null);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSendRequest = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const request = JSON.parse(requestBody);

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (authToken) {
        headers['x-auth-token'] = authToken;
      }

      const proxyBase = await discoveryApi.getBaseUrl('proxy');
      const res = await fetchApi.fetch(`${proxyBase}/open-rpc`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      });

      if (!res.ok) {
        throw new Error(`${res.status} ${res.statusText}`);
      }
      const data = await res.json();
      setResponse(data);
    } catch (err: any) {
      setError(err.message || 'Failed to send request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Try It">
        <Button
          onClick={handleOpen}
          variant="contained"
          color="primary"
          size="small"
          style={{ marginLeft: 8 }}
        >
          <span role="img" aria-label="try-it-inspector">
            🕵️‍♂️
          </span>
          ️️ Try It Now
        </Button>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        classes={{ paper: classes.dialogPaper }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Inspector: {method.name}</DialogTitle>
        <DialogContent>
          <Box className={classes.section}>
            <Typography variant="subtitle2" gutterBottom>
              Request Body
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={10}
              maxRows={20}
              variant="outlined"
              value={requestBody}
              onChange={e => setRequestBody(e.target.value)}
              InputProps={{
                classes: { input: classes.textField },
              }}
            />
          </Box>

          {loading && (
            <Box display="flex" justifyContent="center" my={2}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Box className={classes.section}>
              <Typography variant="subtitle2" gutterBottom color="error">
                Error
              </Typography>
              <Box className={classes.errorBox}>
                <pre>{error}</pre>
              </Box>
            </Box>
          )}

          {response && (
            <Box className={classes.section}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2">
                  Response
                </Typography>
                <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                  <Button size="small" onClick={handleCopyResponse}>
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </Tooltip>
              </Box>
              <Box className={classes.responseBox}>
                <pre>{JSON.stringify(response, null, 2)}</pre>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="default">
            Close
          </Button>
          <Button
            onClick={handleSendRequest}
            color="primary"
            variant="contained"
            disabled={loading || !requestBody}
          >
            Send Request
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InspectorPlugin;
